import classNames from 'classnames';
import { Field, useFormikContext } from 'formik';
import { FieldProps } from 'formik/dist/Field';
import { useIntl } from 'react-intl';
import ReactSelect, { Props as ReactSelectProps, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';
import { OptionsType } from 'react-select/src/types';
import { getFormUnitProps } from '../../../../components/form/base';
import { theme } from '../../../../styles';
import { FormUnit, FormUnitProps } from '../../base/FormUnit';
import './styles.css';

type Props<T, IsMulti extends boolean> = Omit<FormUnitProps, 'children'> &
  // @ts-ignore
  ReactSelectProps<T, IsMulti> & {
    readonly multiple?: boolean;
    readonly disabled?: boolean;
    readonly loadOptions?: (
      inputValue: string,
      // @ts-ignore
      callback: (options: OptionsType<T>) => void,
    ) => Promise<any> | void;
  };

export type SelectProps<T, IsMulti extends boolean> = Props<T, IsMulti>;

export function SelectInput<T, IsMulti extends boolean>(allProps: Props<T, IsMulti>) {
  const { unitProps, rest: props } = getFormUnitProps<Props<T, IsMulti>>(allProps);
  const { ...rest } = props;
  const hasError = !!unitProps.error;

  const { formatMessage } = useIntl();
  const formik = useFormikContext();

  function onChange_getOptionValue(option: any) {
    if (!option) {
      return option;
    }

    if (props.getOptionValue) {
      return props.getOptionValue(option);
    }

    return option.value;
  }

  const commonProps = {
    className: classNames('mi-react-select', hasError && 'mi-react-select--invalid'),
    placeholder: formatMessage({
      id: 'common.actions.select',
      defaultMessage: 'Seleccionar...',
    }),
    classNamePrefix: 'rs',
    isDisabled: rest.disabled || rest.isLoading,
    isLoading: rest.isLoading,
    theme: (_theme: any) => ({
      ..._theme,
      colors: {
        ..._theme.colors,
        //primary25: 'hotpink',
        primary: theme.aqua,
      },
    }),
    isMulti: props.multiple,
    closeMenuOnSelect: !props.multiple,
    onChange: (option: any, action: any) => {
      if (formik && props.name && !props.onChange) {
        formik.setFieldTouched(props.name);

        if (props.isMulti) {
          formik.setFieldValue(props.name, (option as any).map(onChange_getOptionValue));
          return;
        }

        formik.setFieldValue(props.name, onChange_getOptionValue(option));

        return;
      }

      props.onChange && props.onChange(option, action);
    },
    'data-testid': allProps['data-testid'],
    ...rest,
  };

  if (rest.loadOptions) {
    return (
      <FormUnit {...unitProps}>
        {/*@ts-ignore*/}
        <AsyncSelect loadOptions={rest.loadOptions} {...commonProps} />
      </FormUnit>
    );
  }

  return (
    <FormUnit {...unitProps}>
      {/*@ts-ignore*/}
      <ReactSelect {...commonProps} isSearchable={rest.isSearchable || false} />
    </FormUnit>
  );
}

SelectInput.defaultProps = {};

export function SelectField<T extends object, IsMulti extends boolean>(
  p: Props<T, IsMulti> & {
    name: string;
    onChange?: (value: ValueType<T, IsMulti>) => void;
  },
) {
  const { name, ...rest } = p;

  const defaultValue = rest.multiple ? [] : null;

  return (
    <Field name={name}>
      {(fp: FieldProps) => {
        return (
          <SelectInput
            {...fp.field}
            {...rest}
            onChange={(value) => {
              fp.form.setFieldValue(name, value || defaultValue);
              fp.form.setFieldTouched(name);
              p.onChange && p.onChange(value);
            }}
            error={fp.meta.touched && fp.meta.error}
            disabled={rest.disabled}
            // readOnly={fp.form.isSubmitting}
          />
        );
      }}
    </Field>
  );
}
